import { type Component, For, Show, createMemo } from "solid-js";
import type { ArtifactState } from "~/stores/ArtifactStore";
import { ArtifactTabs } from "./ArtifactTabs";
import { ArtifactViewer } from "./ArtifactViewer";

interface ArtifactPanelProps {
  artifacts: ArtifactState[];
  activeArtifactId?: string;
  onArtifactSelect: (id: string) => void;
  onArtifactClose: (id: string) => void;
  class?: string;
}

export const ArtifactPanel: Component<ArtifactPanelProps> = (props) => {
  const activeArtifact = createMemo(() => {
    return props.artifacts.find((a) => a.id === props.activeArtifactId);
  });

  const hasArtifacts = createMemo(() => props.artifacts.length > 0);

  return (
    <div
      class={`h-full bg-gray-50 dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 overflow-auto ${props.class || ""}`}
    >
      <Show when={hasArtifacts()} fallback={<EmptyArtifactPanel />}>
        {/* Fixed Tabs Header - sticky within this scroll container */}
        <div class="sticky top-0 z-10 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <ArtifactTabs
            artifacts={props.artifacts}
            activeId={props.activeArtifactId}
            onSelect={props.onArtifactSelect}
            onClose={props.onArtifactClose}
          />
        </div>

        {/* Content Area - just the artifact content, no separate scroll */}
        <div class="relative min-h-96">
          <Show when={activeArtifact()} fallback={<NoActiveArtifact />}>
            <For each={props.artifacts}>
              {(artifact) => (
                <div class={artifact.id === props.activeArtifactId ? "block" : "hidden"}>
                  <ArtifactViewer artifact={artifact} isActive={artifact.id === props.activeArtifactId} />
                </div>
              )}
            </For>
          </Show>
        </div>
      </Show>
    </div>
  );
};

const EmptyArtifactPanel: Component = () => (
  <div class="flex items-center justify-center p-8 min-h-96">
    <div class="text-center text-gray-500 dark:text-gray-400">
      <div class="text-6xl mb-4">📎</div>
      <h3 class="text-lg font-medium mb-2">No artifacts yet</h3>
      <p class="text-sm">Artifacts like code, images, and documents will appear here when created by the AI.</p>
    </div>
  </div>
);

const NoActiveArtifact: Component = () => (
  <div class="flex items-center justify-center p-8 min-h-96">
    <div class="text-center text-gray-500 dark:text-gray-400">
      <div class="text-4xl mb-4">🔍</div>
      <h3 class="text-lg font-medium mb-2">Select an artifact</h3>
      <p class="text-sm">Click on a tab above to view the artifact content.</p>
    </div>
  </div>
);
